import { useMemo } from 'react';
import type { ReactNode } from 'react';

import type { ApiAddress } from '#/api/types/ApiAddress';
import type { ApiRemoteOptions } from '#/api/types/ApiRemoteOptions';
import { Box } from '#/components/Box/Box';
import { Icon } from '#/components/Icon/Icon';
import { IdealistDayLabel } from '#/components/IdealistDays/IdealistDayLabel';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { Text } from '#/components/Text/Text';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import { useRsvpAnswer } from '#/modules/listing/events/hooks/useRsvpAnswer';
import type { ListingTiming } from '#/modules/listing/helpers/listingTiming';
import type { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import { colors, spacing } from '#/theme/theme';
import { getLocationTypeText } from '#/utils/listings/getLocationTypeText';
import { idealistDateString } from '#/utils/string/datetime/convertDatetimeString';
import { timeago } from '#/utils/string/datetime/timeago';

import { getListingViewHeaderLocationSentence } from './getListingViewHeaderLocationSentence';
import {
  ListingViewHeaderContent,
  ListingViewHeaderContentHolder,
  ListingViewHeaderHolder,
  ListingViewHeaderLocationDescription,
  ListingViewHeaderOrgName,
  ListingViewHeaderTagHolder,
  ListingViewHeaderTitle,
} from './ListingViewHeader.styled';
import { ListingViewHeaderDoneInADayTag } from './ListingViewHeaderDoneInADayTag';

type Props = {
  address: ApiAddress;
  hasRsvp?: boolean;
  isPostedAnonymously: boolean;
  isPreview?: boolean;
  listingId: string;
  listingName: string;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  timing: ListingTiming | null;
  orgName: string | null;
  orgType: ApiOrgType | null;
  orgUrl: string | null;
  remoteOptions: ApiRemoteOptions;
  isIdealistDay?: boolean;
  canBeDoneInADay?: boolean;
  actionButtons: ReactNode;
  hideLocationSentence?: boolean;
};

export function ListingViewHeader({
  address,
  hasRsvp,
  isPostedAnonymously,
  isPreview = false,
  listingId,
  listingName,
  listingType,
  timing,
  orgName,
  orgType,
  orgUrl,
  remoteOptions,
  isIdealistDay,
  canBeDoneInADay,
  actionButtons,
  hideLocationSentence,
}: Props) {
  const { i18n, locale } = useI18n();

  // initialize rsvp buttons if this listing has rsvp enabled
  useRsvpAnswer(listingId, hasRsvp);

  const locationSentence = useMemo(
    () =>
      getListingViewHeaderLocationSentence({
        i18n,
        listingType,
        remoteOptions,
        address,
      }),
    [i18n, address, listingType, remoteOptions],
  );

  return (
    <ListingViewHeaderHolder>
      <ListingTypeLabel listingType={listingType} orgType={orgType} />

      <div style={{ display: 'flex', alignItems: 'center', gap: spacing[16] }}>
        {timing?.publishedDate && !isPreview && (
          <ListingViewHeaderTagHolder
            style={{ minWidth: 240 }}
            $color={timing.isNew ? 'lightYellow' : 'backgroundGrey'}
          >
            {timing.isNew && (
              <div>
                <Text
                  fontWeight="semiBold"
                  color="mediumContentGrey"
                  display="inline-block"
                  lineHeight="6px"
                  ml="0px"
                  mr="12px"
                  p="0px"
                  style={{ borderBottom: `4px solid ${colors.brandYellow}` }}
                >
                  {i18n('New')}
                </Text>
              </div>
            )}

            <Text
              display="inline-block"
              color={timing.isNew ? 'accessibleYellow' : 'darkContentGrey'}
              qaId="listing-header-published-since"
              data-published-date={idealistDateString(timing.publishedDate)}
            >
              {
                /* TRANSLATORS: Listing page - time published */
                i18n('Published %(timeAgo)s', {
                  timeAgo: timeago(timing.publishedDate, {
                    now: timing.now,
                    locale,
                  }),
                })
              }
            </Text>
          </ListingViewHeaderTagHolder>
        )}

        {canBeDoneInADay && (
          <Box display="inline">
            <ListingViewHeaderTagHolder $color="lightPurple">
              <ListingViewHeaderDoneInADayTag />
            </ListingViewHeaderTagHolder>
          </Box>
        )}
      </div>

      <ListingViewHeaderContentHolder>
        <ListingViewHeaderContent>
          {isIdealistDay && <IdealistDayLabel />}

          <ListingViewHeaderTitle data-qa-id="listing-name">
            {listingName}
            {isPostedAnonymously && (
              <Box display="inline-block" ml="6px" verticalAlign="super">
                <LegacyTooltip>
                  {i18n(
                    `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                  )}
                </LegacyTooltip>
              </Box>
            )}
          </ListingViewHeaderTitle>

          {orgName && (
            <ListingViewHeaderOrgName>
              {orgUrl ? (
                <StyledNativeLinkWithRef href={orgUrl} qaId="org-link">
                  {orgName}
                </StyledNativeLinkWithRef>
              ) : (
                orgName
              )}
            </ListingViewHeaderOrgName>
          )}

          <ListingViewHeaderLocationDescription>
            <Icon name="location-filled" size={20} colorName="dustyBlue" />

            <div>
              <span>
                {getLocationTypeText(
                  i18n,
                  listingType,
                  remoteOptions.locationType,
                )}
              </span>

              {!hideLocationSentence && (
                <span data-qa-id="location-sentence">, {locationSentence}</span>
              )}
            </div>
          </ListingViewHeaderLocationDescription>

          {actionButtons}
        </ListingViewHeaderContent>
      </ListingViewHeaderContentHolder>
    </ListingViewHeaderHolder>
  );
}
